import { useMemo } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, InputAdornment, styled, TextField } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useSnackbar } from 'notistack';
import { TFormType, TProjectFormData } from 'src/@types';
import { IProject } from 'src/@types/apiResponseTypes';
import { projectApi } from 'src/api';
import DepartmentSelect from 'src/components/DepartmentSelect';
import { RHFSelect, RHFTextField } from 'src/components/hook-form';
import FormProvider from 'src/components/hook-form/FormProvider';
import RHFDatePicker from 'src/components/hook-form/RHFDatePicker';
import RHFEmployeeSelect from 'src/components/RHFEmployeeSelect';
import { useStores } from 'src/models';
import { COMMON_CODE_NAME, PROJECT_MNG_TYPE_CD } from 'src/static/constants';
import projectFormValidationSchema from 'src/static/yup/projectFormValidationSchema';
import { convertCommonCodeToOptions } from 'src/utils/convertCodeToOtions';
import { getProjectFormDefaultValues } from 'src/utils/getRHFDefaultValues';
import { numberToSimpleKoreanCurrency } from 'src/utils/koreanCurrency';

import ProjectRoundSalesSection from './ProjectRoundSalesSection';
import PurchaseAssignmentSection from './PurchaseAssignmentSection';
import PurchaseOtherSection from './PurchaseOtherSection';
import RHFClientSelect from './RHFClientSelect';

export interface IProjectFormProps {
  readonly?: boolean;
  formId: string;
  formType: TFormType;
  project?: IProject;
  onComplete?: () => void;
}

/**
 * ## ProjectForm 설명
 * - 프로젝트 등록/수정 폼
 * - PROJECT_MNG_TYPE_CD에 따라 표시되는 섹션이 다름
 *   - 선택 없음: 섹션 미표시
 *   - 과제(ASSIGNMENT): 매입내역(purchaseAssignment)만 표시
 *   - 그 외: 기성내역 + 매입내역(purchaseOther)
 */
function ProjectForm({ readonly, formId, formType, project, onComplete }: IProjectFormProps) {
  const { enqueueSnackbar } = useSnackbar();
  const { commonCodeStore, departmentStore } = useStores();
  const { departments } = departmentStore;
  const resolver = yupResolver(projectFormValidationSchema);

  const projectTypeOptions = convertCommonCodeToOptions(
    commonCodeStore.getCCodeByName(COMMON_CODE_NAME.PROJECT_TYPE_CD),
  );
  const projectMngTypeOptions = convertCommonCodeToOptions(
    commonCodeStore.getCCodeByName(COMMON_CODE_NAME.PROJECT_MNG_TYPE_CD),
  );
  const projectStatusOptions = convertCommonCodeToOptions(
    commonCodeStore.getCCodeByName(COMMON_CODE_NAME.PROJECT_STATUS_CD),
  );

  // useForm methods
  const methods = useForm<TProjectFormData>({
    defaultValues: getProjectFormDefaultValues(project),
    resolver,
    mode: 'all',
  });

  // 프로젝트 관리 구분 watch
  const watchProjectMngTypeCd = useWatch({ control: methods.control, name: 'projectMngTypeCd' });

  // 프로젝트 관리 구분 선택 여부
  const hasProjectMngType = useMemo(() => {
    return !!watchProjectMngTypeCd;
  }, [watchProjectMngTypeCd]);

  // 과제 여부 확인
  const isAssignment = useMemo(() => {
    return watchProjectMngTypeCd === PROJECT_MNG_TYPE_CD.ASSIGNMENT;
  }, [watchProjectMngTypeCd]);

  // 발행일이 미래 날짜인지 확인하는 함수
  const isFutureDate = (timestamp: number): boolean => {
    const today = new Date();
    const issueDate = new Date(timestamp);

    today.setHours(0, 0, 0, 0);
    issueDate.setHours(0, 0, 0, 0);

    return issueDate.getTime() > today.getTime();
  };

  // 등록 혹은 수정 시 처리 함수
  const onSubmit = async (projectInfo: TProjectFormData) => {
    try {
      let success = false;

      // 계약 구분에 따라 해당하지 않는 필드 초기화
      const submitData = { ...projectInfo };
      const mngTypeCd = submitData.projectMngTypeCd;

      if (!mngTypeCd) {
        // 선택 없음: 매입내역 초기화 (기성내역은 유지)
        submitData.purchaseAssignments = [];
        submitData.purchaseOthers = [];
      } else if (Number(mngTypeCd) === PROJECT_MNG_TYPE_CD.ASSIGNMENT) {
        // 과제: 기성내역, 그 외 매입내역 초기화
        submitData.projectRoundSales = [];
        submitData.purchaseOthers = [];
      } else {
        // 그 외: 과제용 매입내역 초기화
        submitData.purchaseAssignments = [];
      }

      if (project?.projectSid && formType === 'UPDATE') {
        const result = await projectApi.updateProject(project.projectSid, {
          ...project,
          ...submitData,
        } as TProjectFormData);
        if (result.success) {
          enqueueSnackbar('프로젝트가 수정되었습니다.', { variant: 'success' });
          success = true;
        } else {
          enqueueSnackbar(result.errorMessage, { variant: 'error' });
        }
      } else {
        const result = await projectApi.createProject(submitData);
        if (result.success) {
          enqueueSnackbar('프로젝트가 등록되었습니다.', { variant: 'success' });
          success = true;
        } else {
          enqueueSnackbar(result.errorMessage, { variant: 'error' });
        }
      }

      // 성공 시에만 모달 닫기
      if (success && onComplete) {
        onComplete();
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar('요청 처리 중 오류가 발생했습니다.', { variant: 'error' });
    }
  };

  return (
    <StyledFormProvider
      methods={methods}
      onSubmit={methods.handleSubmit(onSubmit, (error) => {
        console.error(error);
      })}
      formId={formId}
    >
      {/* 기본정보 */}
      <StyledInnerFormContainer>
        <StyledLabel>기본정보</StyledLabel>
        <RowContainer>
          <RHFTextField name="projectNm" label="프로젝트명" inputProps={{ readOnly: readonly }} />
        </RowContainer>
        <RowContainer>
          <RHFEmployeeSelect
            name="bo"
            label="사업PM"
            multiple={false}
            returnType="number"
            readOnly={readonly}
          />
          <RHFEmployeeSelect
            name="pmList"
            label="개발PM"
            multiple={false}
            alwaysArray
            readOnly={readonly}
          />
        </RowContainer>
        <RowContainer>
          <Controller
            control={methods.control}
            name="deprtCd"
            render={({ field, fieldState: { error } }) => (
              <DepartmentSelect
                size="medium"
                label="프로젝트 담당 부서"
                value={field.value || undefined}
                onChange={(value) => {
                  field.onChange(value || undefined);
                }}
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
          <RHFClientSelect name="customerCmpy" label="고객사" readOnly={readonly} />
        </RowContainer>
        <RowContainer>
          <RHFSelect
            name="projectTypeCd"
            label="구분"
            options={projectTypeOptions}
            inputProps={{ readOnly: readonly }}
          />
          <RHFSelect
            name="projectStatusCd"
            label="프로젝트 상태"
            options={projectStatusOptions}
            inputProps={{ readOnly: readonly }}
          />
        </RowContainer>
        <RowContainer>
          <RHFDatePicker name="startDay" label="시작일" readOnly={readonly} />
          <RHFDatePicker name="endDay" label="종료일" readOnly={readonly} />
        </RowContainer>
        <RowContainer>
          <RHFEmployeeSelect name="mngList" label="참여자" readOnly={readonly} />
          <RHFEmployeeSelect name="alertList" label="Slack 알림 담당자" readOnly={readonly} />
        </RowContainer>
      </StyledInnerFormContainer>

      {/* 계약정보 */}
      <StyledInnerFormContainer>
        <StyledLabel>계약정보</StyledLabel>
        <RowContainer>
          <RHFTextField name="cntrctNm" label="계약명" inputProps={{ readOnly: readonly }} />
          <RHFClientSelect name="contractCmpy" label="계약사" readOnly={readonly} />
        </RowContainer>
        <RowContainer>
          <RHFSelect
            name="projectMngTypeCd"
            label="프로젝트 관리 구분"
            options={[{ label: '선택없음', value: '' }, ...projectMngTypeOptions]}
            inputProps={{ readOnly: readonly }}
          />
          <RHFTextField name="projectMngId" label="프로젝트 관리 ID" />
          <Controller
            name="contractAmount"
            control={methods.control}
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <TextField
                {...field}
                label="계약금액"
                name="contractAmount"
                fullWidth
                value={field.value ? field.value.toLocaleString() : ''}
                placeholder="계약금액"
                error={!!error}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                  }
                }}
                onChange={(e) => {
                  const value = e.target.value;
                  const numericValue = Number(value.replace(/,/g, ''));
                  if (!isNaN(numericValue)) {
                    field.onChange(numericValue);
                  }
                }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">원</InputAdornment>,
                  readOnly: readonly,
                }}
                helperText={
                  error
                    ? error?.message
                    : field.value
                    ? `${numberToSimpleKoreanCurrency(field.value)}`
                    : ''
                }
                inputRef={ref}
              />
            )}
          />
        </RowContainer>
      </StyledInnerFormContainer>

      {/* 기성내역: 과제가 아닌 경우 */}
      {!isAssignment && (
        <StyledInnerFormContainer>
          <StyledLabel>기성내역</StyledLabel>
          <ProjectRoundSalesSection methods={methods} readonly={readonly} />
        </StyledInnerFormContainer>
      )}

      {/* 매입내역: 계약 구분이 선택된 경우 */}
      {hasProjectMngType && (
        <StyledInnerFormContainer>
          <StyledLabel>매입내역</StyledLabel>
          {isAssignment ? (
            <PurchaseAssignmentSection methods={methods} readonly={readonly} />
          ) : (
            <PurchaseOtherSection methods={methods} readonly={readonly} />
          )}
        </StyledInnerFormContainer>
      )}

      {/* 비고/이슈 */}
      <RowContainer>
        <RHFTextField
          name="descr"
          label="비고/이슈"
          minRows={4}
          multiline
          inputProps={{ readOnly: readonly }}
        />
      </RowContainer>
    </StyledFormProvider>
  );
}

export default observer(ProjectForm);

const StyledFormProvider = styled(FormProvider)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  padding: theme.spacing(2),
}));

const RowContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),

  '& > *': {
    flex: 1,
  },
}));

const StyledInnerFormContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
}));

const StyledLabel = styled('span')(({ theme }) => ({
  fontSize: '1rem',
  fontWeight: 700,
  padding: theme.spacing(0, 1),
  color: theme.palette.text.secondary,
  position: 'absolute',
  top: '-12px',
  left: '20px',
  background: theme.palette.background.paper,
}));
